import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Bell, CheckCheck, CircleAlert, CircleCheck, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { HeaderNotificationItem } from '@/types/header';

type Severity = HeaderNotificationItem['severity'];

const severityIcon: Record<Severity, typeof CircleAlert> = {
  critical: CircleAlert,
  warning: CircleAlert,
  success: CircleCheck,
  info: Info,
};

const severityIconCls: Record<Severity, string> = {
  critical: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  success: 'text-lime-600 dark:text-lime-400',
  info: 'text-blue-600 dark:text-blue-400',
};

const severityPillCls: Record<Severity, string> = {
  critical: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  success: 'bg-lime-50 text-lime-700 dark:bg-lime-400/15 dark:text-lime-300',
  info: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
};

const TRIGGER_BASE_CLS =
  'relative inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2';
const TRIGGER_DEFAULT_VISUAL_CLS =
  'size-9 rounded-md text-slate-400 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-100';

interface HeaderNotificationsProps {
  items: HeaderNotificationItem[];
  unreadCount: number;
  isLoading?: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  className?: string;
}

function formatRelativeTimestamp(timestamp: string | undefined, isFrench: boolean): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.floor(diffMs / (1000 * 60)));

  if (diffMin < 60) return isFrench ? `Il y a ${diffMin} min` : `${diffMin} min ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return isFrench ? `Il y a ${diffHours} h` : `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return isFrench ? `Il y a ${diffDays} j` : `${diffDays}d ago`;
}

export function HeaderNotifications({
  items,
  unreadCount,
  isLoading = false,
  onMarkAsRead,
  onMarkAllAsRead,
  className,
}: HeaderNotificationsProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const isFrench = i18n.language.startsWith('fr');

  const unreadItems = useMemo(() => items.filter((item) => !item.read).length, [items]);

  const goToNotification = (item: HeaderNotificationItem) => {
    onMarkAsRead(item.id);
    setOpen(false);
    if (item.href) {
      navigate(item.href);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('header_notifications')}
          className={cn(TRIGGER_BASE_CLS, TRIGGER_DEFAULT_VISUAL_CLS, className)}
        >
          <Bell
            className={cn(
              'size-4',
              unreadCount > 0 && 'animate-bell-ring text-lime-600 dark:text-lime-400',
            )}
            strokeWidth={1.75}
          />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white shadow-[0_0_0_1.5px_var(--bg-topbar,#fff)] dark:shadow-[0_0_0_1.5px_rgba(13,13,26,0.78)]">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[min(24rem,92vw)] border border-slate-200 bg-white p-0 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_8px_-4px_rgba(15,23,42,0.06)] dark:border-charcoal-700 dark:bg-charcoal-850"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-charcoal-800">
          <div>
            <p className="text-[14px] font-semibold tracking-[-0.01em] text-slate-950 dark:text-slate-50">
              {t('header_notifications')}
            </p>
            <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400">
              {t('header_notifications_unread', { count: unreadItems })}
            </p>
          </div>
          <button
            type="button"
            onClick={onMarkAllAsRead}
            disabled={items.length === 0 || unreadItems === 0}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium text-slate-500 transition-colors hover:bg-slate-900/5 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-50 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-100"
          >
            <CheckCheck className="size-3.5" strokeWidth={1.75} />
            {t('header_mark_all_read')}
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {isLoading ? (
            <p className="px-2 py-6 text-center text-[13px] text-slate-500">{t('loading')}</p>
          ) : items.length === 0 ? (
            <p className="px-2 py-6 text-center text-[13px] text-slate-500">
              {t('header_no_notifications')}
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {items.map((item) => {
                const SeverityIcon = severityIcon[item.severity];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goToNotification(item)}
                    className={cn(
                      'group flex w-full flex-col gap-1 rounded-lg px-3 py-2.5 text-left transition-colors',
                      item.read
                        ? 'bg-transparent hover:bg-slate-50 dark:hover:bg-white/5'
                        : 'bg-slate-50 hover:bg-slate-100 dark:bg-white/5 dark:hover:bg-white/[0.07]',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex min-w-0 flex-1 items-start gap-2">
                        <SeverityIcon
                          className={cn('mt-0.5 size-4 shrink-0', severityIconCls[item.severity])}
                          strokeWidth={1.75}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-semibold tracking-[-0.005em] text-slate-950 dark:text-slate-50">
                            {item.title}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-[12px] text-slate-500 dark:text-slate-400">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <span
                        className={cn(
                          'inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                          severityPillCls[item.severity],
                        )}
                      >
                        {t(`header_notif_severity_${item.severity}`)}
                      </span>
                    </div>

                    {item.timestamp && (
                      <p className="ml-6 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                        {formatRelativeTimestamp(item.timestamp, isFrench)}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
